import type {
    FieldValueDTO,
    SubmitClientDump,
    SubmitMainDump,
    SubmitNamedId,
    VisitCardDTO,
} from "@/api/visit_card_client"

export interface MainTextFieldSpec {
    flatKey: string
    /** Path on VisitCardDTO for FieldValueDTO fields; null for salon (UI-only). */
    path: readonly string[] | null
    label: string
}

/** Main-tab text fields (без Combobox). Салон — только локальный UI. */
export const MAIN_TEXT_FIELDS: readonly MainTextFieldSpec[] = [
    { flatKey: "designer", path: ["designer"], label: "Сотрудник" },
    { flatKey: "salon", path: null, label: "Салон" },
    { flatKey: "dealer", path: ["dealer"], label: "Дилер" },
    { flatKey: "ad_source", path: ["ad_source"], label: "Источник рекламы" },
    { flatKey: "brand", path: ["brand"], label: "Бренд" },
    { flatKey: "product_group", path: ["product_group"], label: "Товарная группа" },
    { flatKey: "refusal_reason", path: ["refusal_reason"], label: "Причина отказа" },
] as const

export interface VisitCardSubmitPayload {
    profile_name: string
    client: SubmitClientDump
    main: SubmitMainDump
}

export function getField(card: VisitCardDTO, path: readonly string[]): FieldValueDTO | null {
    let cursor: unknown = card
    for (const key of path) {
        cursor = (cursor as Record<string, unknown> | null | undefined)?.[key]
        if (cursor == null) return null
    }
    return cursor as FieldValueDTO
}

function nullIfEmpty(value: string | null | undefined): string | null {
    const trimmed = value?.trim() ?? ""
    return trimmed ? trimmed : null
}

function namedId(field: FieldValueDTO | undefined | null): SubmitNamedId | null {
    if (!field) return null
    const id = field.id ?? null
    const name = nullIfEmpty(field.name)
    if (id == null && name == null) return null
    return { id, name }
}

function mainVisibleText(
    spec: MainTextFieldSpec,
    card: VisitCardDTO,
    mainLocal: Record<string, string>,
): string | null {
    if (Object.prototype.hasOwnProperty.call(mainLocal, spec.flatKey)) {
        return nullIfEmpty(mainLocal[spec.flatKey])
    }
    if (!spec.path) return null
    const field = getField(card, spec.path)
    return nullIfEmpty(field?.name ?? field?.raw ?? "")
}

export function buildSubmitPayload(
    card: VisitCardDTO,
    mainLocal: Record<string, string>,
    profileName?: string,
): VisitCardSubmitPayload {
    const byKey = (flatKey: string) => MAIN_TEXT_FIELDS.find((s) => s.flatKey === flatKey)!
    return {
        profile_name: profileName ?? "",
        client: {
            last_name: nullIfEmpty(card.client?.last_name),
            first_name: nullIfEmpty(card.client?.first_name),
            patronymic: nullIfEmpty(card.client?.patronymic),
            phone: nullIfEmpty(card.client?.phone),
            email: nullIfEmpty(card.client?.email),
            juridical_type: namedId(card.client?.juridical_type),
            social_status: namedId(card.client?.social_status),
        },
        main: {
            designer: mainVisibleText(byKey("designer"), card, mainLocal),
            salon: mainVisibleText(byKey("salon"), card, mainLocal),
            dealer: mainVisibleText(byKey("dealer"), card, mainLocal),
            ad_source: mainVisibleText(byKey("ad_source"), card, mainLocal),
            brand: mainVisibleText(byKey("brand"), card, mainLocal),
            product_group: mainVisibleText(byKey("product_group"), card, mainLocal),
            refusal_reason: mainVisibleText(byKey("refusal_reason"), card, mainLocal),
            comments: nullIfEmpty(card.comments),
        },
    }
}
